// Ablagefeld (Handoff §4: Anhänge, CSV, Belegungslisten): große Karte mit gestricheltem Innenrand,
// Drag & Drop + Klick. Welche Dateien gelten, entscheidet das Formular (Quelle wird mitgeliefert,
// weil manche Formulare beim Ablegen anders prüfen als bei der Auswahl).
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import type { Icon } from '@phosphor-icons/react';
import { ABSCHNITT, FORM, GLANZ } from './form-stil';

export interface FormAblageProps {
  /** Fette Zeile (Text aus dem Code). */
  titel?: ReactNode;
  /** Fette Zeile, solange eine Datei darübergezogen wird (nur wenn der Code eine hat). */
  titelBeimZiehen?: ReactNode;
  text?: ReactNode;
  hinweis?: ReactNode;
  icon: Icon;
  /** Verlauf der Icon-Kachel */
  verlauf: string;
  accept?: string;
  multiple?: boolean;
  onDateien: (dateien: File[], quelle: 'auswahl' | 'ablage') => void;
  gesperrt?: boolean;
  tip?: string;
  /** Screenreader-Bezeichnung des Datei-Knopfs */
  ariaLabel: string;
  /** Niedriger, wenn schon Dateien darunter stehen. */
  kompakt?: boolean;
  grossesIcon?: boolean;
}

export function FormAblage({
  titel, titelBeimZiehen, text, hinweis, icon: I, verlauf, accept, multiple, onDateien, gesperrt, tip, ariaLabel, kompakt, grossesIcon,
}: FormAblageProps) {
  const input = useRef<HTMLInputElement>(null);
  const [ziehen, setZiehen] = useState(false);
  const fallen = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setZiehen(false);
    if (gesperrt) return;
    const dateien = Array.from(e.dataTransfer.files);
    if (dateien.length) onDateien(dateien, 'ablage');
  };
  const kachel = grossesIcon ? 64 : 54;
  return (
    <>
      <button type="button" aria-label={ariaLabel} data-tip={tip} disabled={gesperrt}
        onClick={() => input.current?.click()}
        onDragEnter={(e) => { e.preventDefault(); if (!gesperrt) setZiehen(true); }}
        onDragOver={(e) => { e.preventDefault(); if (!gesperrt && !ziehen) setZiehen(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setZiehen(false); }}
        onDrop={fallen}
        className="pf-rise relative flex w-full shrink-0 cursor-pointer flex-col items-center justify-center overflow-hidden border-none text-center disabled:cursor-not-allowed"
        style={{
          gap: 10, padding: 12, borderRadius: 24, font: 'inherit', color: FORM.text, minHeight: kompakt ? 150 : 260,
          background: 'linear-gradient(165deg,rgba(95,211,138,0.13),rgba(255,255,255,0.035) 55%,rgba(255,255,255,0.02))',
          boxShadow: ABSCHNITT.schatten, opacity: gesperrt ? 0.5 : 1,
          transform: ziehen ? 'scale(1.01)' : 'none', transition: 'transform .3s, min-height .4s cubic-bezier(.32,.72,0,1)',
        }}>
        <span aria-hidden className="pointer-events-none absolute" style={{
          inset: 10, borderRadius: 18, transition: 'all .25s',
          border: `1.5px dashed ${ziehen ? FORM.gold : 'rgba(255,255,255,0.2)'}`,
          background: ziehen ? 'rgba(226,192,141,0.14)' : 'rgba(0,0,0,0.18)',
        }} />
        <span aria-hidden className="pointer-events-none relative flex items-center justify-center overflow-hidden" style={{
          width: kachel, height: kachel, borderRadius: grossesIcon ? 20 : 17, background: verlauf,
          boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.5),inset 0 -2px 4px rgba(0,0,0,0.2),0 8px 16px -8px rgba(0,0,0,0.6)',
        }}>
          <span className="absolute inset-0" style={{ background: GLANZ }} />
          <span className="absolute rounded-full" style={{ width: kachel * 0.7, height: kachel * 0.7, right: -kachel * 0.22, bottom: -kachel * 0.26, background: 'rgba(255,255,255,0.16)' }} />
          <I size={grossesIcon ? 32 : 27} weight="fill" color="#fff" className="relative" />
        </span>
        {(titel || titelBeimZiehen) && (
          <span className="pointer-events-none relative" style={{ fontSize: 16, fontWeight: 800 }}>{ziehen && titelBeimZiehen ? titelBeimZiehen : titel}</span>
        )}
        {text && <span className="pointer-events-none relative" style={{ maxWidth: 460, fontSize: 13.5, fontWeight: 700, lineHeight: 1.45 }}>{text}</span>}
        {hinweis && <span className="pointer-events-none relative" style={{ fontSize: 12.5, fontWeight: 600, color: FORM.text2 }}>{hinweis}</span>}
      </button>
      <input ref={input} type="file" accept={accept} multiple={multiple} hidden
        onChange={(e) => {
          const d = Array.from(e.target.files ?? []);
          if (d.length) { onDateien(d, 'auswahl'); }
          // Feld leeren, damit dieselbe Datei erneut gewählt werden kann
          e.target.value = '';
        }} />
    </>
  );
}
